import { useApiQuery } from '@vrplatform/data';
import type { ListingRow } from '../../../../listings/list/ledger/useListingsTableQuery';
import type { Entity } from './_types';
import type { Maybe } from '@vrplatform/utils';

export const formatReservationToEntity = (reservation: {
  id: string;
  confirmationCode?: Maybe<string>;
  connection: Maybe<{
    app: Maybe<{
      icon: Maybe<string>;
    }>;
  }>;
  guestName?: Maybe<string>;
}): Entity => ({
  id: reservation.id,
  description: reservation.confirmationCode || '-',
  icon: reservation.connection?.app?.icon,
  title: reservation.guestName || 'No name',
  type: 'reservation',
});

export const STORYBOOK_EXPENSE_SELECT_RESERVATION_LISTING_MODAL_KEY =
  'expense-select-reservation-listing-modal';

export const formatListingToEntity = (listing: ListingRow): Entity => ({
  id: listing.id,
  description: listing.name || '-',
  icon: undefined,
  title: listing.name || 'No name',
  type: 'listing',
});

export const useEntityQuery = (props: {
  activeEntity: {
    id: string;
    type: Entity['type'];
  } | null;
}) => {
  return useApiQuery(
    async (api, signal, args, hasura): Promise<Entity | null> => {
      if (!args.activeEntity) return null;

      if (args.activeEntity.type === 'reservation') {
        const [reservation, apps] = await Promise.all([
          api
            .GET('/reservations/{id}', {
              params: {
                path: {
                  id: args.activeEntity.id,
                },
              },
              signal,
            })
            .then((res) => {
              if (res.error?.code !== 'NOT_FOUND') throw res.error;
              if (!res.data) return null;
              return res.data;
            }),
          hasura(
            (q) =>
              q.app().map((app) => ({
                id: app.id,
                name: app.name,
                icon: app.iconRound,
              })),
            {
              fetchOptions: {
                signal,
              },
            }
          ),
        ]);

        if (!reservation) return null;

        const data = {
          ...reservation,
          connection: reservation.connection
            ? {
                ...reservation.connection,
                app: apps.find(
                  (app) => app.id === reservation.connection?.appId
                ),
              }
            : undefined,
        };

        return formatReservationToEntity(data);
      }

      return await api
        .GET('/listings/{id}', {
          params: {
            path: {
              id: args.activeEntity.id,
            },
          },
          signal,
        })
        .then((res) => {
          if (res.error?.code !== 'NOT_FOUND') throw res.error;
          if (!res.data) return null;

          return formatListingToEntity(res.data);
        });
    },
    {
      queryGroup: 'expenses',
      uniqueQueryKey: 'expense-select-reservation-modal',
      variables: props,
      queryKeys: ['reservations'],
      keepPreviousData: true,
      storybookQueryKey: STORYBOOK_EXPENSE_SELECT_RESERVATION_LISTING_MODAL_KEY,
    }
  );
};
